// ─── URLs for the admin console and the school portal ────────────────────────
// Every admin and portal screen used to live at /admin (title "Admin ·
// YogaStay" even inside a school's portal): nothing could be linked, bookmarked
// or opened in a new tab. Each view now has a path, and the Schools table keeps
// its filter, search, sort and page in the query string:
//
//   /admin/<tab>                          admin tab (overview, schools, …)
//   /admin/schools?status=pending&sort=registered_desc&page=2&q=bali
//   /admin/schools/<id>                   Schools with that school's drawer open
//   /admin/as/<schoolId>/<portalTab>      managing a school, on one portal page
//   /dashboard/<portalTab>                a school's own portal
//
// Pure functions; the components keep the address bar in step with
// history.replaceState, so the existing back-button handling is unchanged.

const segs = (p) => String(p || "").split("/").filter(Boolean).map(decodeURIComponent);

/** null when the path is not an admin path. */
export function parseAdminLocation(pathname, search = "") {
  const parts = segs(pathname);
  if (parts[0] !== "admin") return null;
  const q = new URLSearchParams(search);
  if (parts[1] === "as" && parts[2]) return { manageSchoolId: parts[2], portalTab: parts[3] || "home" };
  const tab = parts[1] || "overview";
  return {
    tab,
    schoolId: tab === "schools" && parts[2] ? parts[2] : null,
    status: q.get("status") || null,
    sort: q.get("sort") || null,
    page: Math.max(1, parseInt(q.get("page"), 10) || 1),
    q: q.get("q") || "",
    // Schools filters (B5); empty = any.
    filters: Object.fromEntries(FILTER_KEYS.map(k => [k, q.get(k) || ""]).filter(([, v]) => v)),
  };
}

/** The Schools extra filters kept in the URL. */
export const FILTER_KEYS = ["country", "city", "from", "to", "doc", "listings", "source"];

/** The path + query for an admin state. Defaults are left out so URLs stay short. */
export function adminPath({ tab = "overview", schoolId = null, manageSchoolId = null, portalTab = "home", status, sort, page, q, filters } = {}) {
  if (manageSchoolId) return `/admin/as/${encodeURIComponent(manageSchoolId)}/${encodeURIComponent(portalTab || "home")}`;
  let path = `/admin/${encodeURIComponent(tab)}`;
  if (tab === "schools" && schoolId) path += `/${encodeURIComponent(schoolId)}`;
  if (tab !== "schools") return path;
  const params = new URLSearchParams();
  if (status && status !== "needs_action") params.set("status", status);   // Needs action is the default view
  if (sort) params.set("sort", sort);
  if (page && page > 1) params.set("page", String(page));
  if (q) params.set("q", q);
  FILTER_KEYS.forEach(k => { if (filters && filters[k]) params.set(k, filters[k]); });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** The portal tab in /dashboard/<tab> or /admin/as/<id>/<tab>; null when neither. */
export function portalTabFromPath(pathname) {
  const parts = segs(pathname);
  if (parts[0] === "dashboard") return parts[1] || "home";
  if (parts[0] === "admin" && parts[1] === "as" && parts[2]) return parts[3] || "home";
  return null;
}

export const portalPath = (tab, manageSchoolId = null) =>
  manageSchoolId ? adminPath({ manageSchoolId, portalTab: tab }) : `/dashboard/${encodeURIComponent(tab || "home")}`;

/** DataTable sort state ↔ "registered_desc". created_at reads as "registered". */
export function sortToParam(sort) {
  if (!sort?.key) return null;
  const key = sort.key === "created_at" ? "registered" : sort.key;
  return `${key}_${sort.dir === "desc" ? "desc" : "asc"}`;
}
export function paramToSort(param) {
  const m = String(param || "").match(/^([a-z_]+?)_(asc|desc)$/);
  if (!m) return null;
  return { key: m[1] === "registered" ? "created_at" : m[1], dir: m[2] };
}

/** Replace the address bar without a new history entry (no-op when unchanged). */
export function syncUrl(path) {
  try {
    if (typeof window === "undefined" || !path) return;
    if (window.location.pathname + window.location.search === path) return;
    window.history.replaceState(window.history.state, "", path);
  } catch {}
}

/** "Schools · Admin · YogaStay" — most specific first. */
export function setPageTitle(...parts) {
  try { document.title = [...parts.filter(Boolean), "YogaStay"].join(" · "); } catch {}
}

/** Paths that need a signed-in user. */
export const isProtectedPath = (pathname) => /^\/(admin|dashboard)(\/|$)/.test(String(pathname || ""));

/** Only same-site, protected paths are honoured as ?next= (no open redirect). */
export function safeNext(next) {
  const n = String(next || "");
  return n.startsWith("/") && !n.startsWith("//") && isProtectedPath(n.split("?")[0]) ? n : null;
}

/**
 * Props for a sidebar link that is a real <a href> (open in new tab works) but
 * switches in place on a plain click.
 */
export function navLinkProps(href, onNavigate) {
  return {
    href,
    onClick: (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      onNavigate();
    },
  };
}
